import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Collapse, Modal, Select, Skeleton, Table, Tabs, Tooltip, Upload, message } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import { ColumnsType } from 'antd/lib/table'
import {
  DownloadOutlined,
  HistoryOutlined,
  HomeOutlined,
  IdcardOutlined,
  InboxOutlined,
  CloudSyncOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { useAuth, useProposal, useStep } from 'hooks'
import { timeAsDayjs } from 'utils/time'
import AntifraudEgiRepository from 'egi/repositories/AntifraudEgiRepository'
import { colors, getTextColor } from 'styles/colors'
import { renderBadge } from 'utils/renderBadge'
import { apiEventResultLines, apiEventTitle, changeText, historyTimeline } from './antifraudApiHistory'
import {
  canReconcileUnico,
  canResendEmailAge,
  mapParticipants,
  participantRole,
  participantTabStatus,
  showsFraudWarning,
  summarizeAntifraudStep
} from './antifraudAnalysisMapper'
import { buildReportData, generateAntifraudReport } from './antifraudAnalysisReport'
import {
  acertPixResendAfterUnicoAllowed,
  canActOnAntifraud,
  processResendEnabled,
  resendIsBlocked
} from './antifraudPermissions'
import { hasErrors, requiresJustification, validateManualReview } from './antifraudReviewRules'
import {
  IAntifraudApiEvent,
  IAntifraudManualReviewState,
  IAntifraudSavedReview,
  IAntifraudStepSummary,
  _resendableSolution,
  IAntifraudParticipant,
  IAntifraudProcess,
  _antifraudManualReview,
  _antifraudParticipantStatus,
  _antifraudStepDecision,
  _participantRole
} from './antifraudAnalysisTypes'

const STYLE = 'antifraud__'

const participantInfo: {
  [key in _antifraudParticipantStatus]: { color: string, text: string }
} = {
  approved: { color: colors.approved, text: 'Aprovado' },
  lowRisk: { color: colors.updated, text: 'Risco baixo' },
  reproved: { color: colors.reproved, text: 'Reprovado' },
  partialReproval: { color: colors.updated, text: 'Reprova parcial' },
  retryable: { color: colors.updated, text: 'Erro na execução' },
  needsReview: { color: colors.analysis, text: 'Requer análise manual' },
  awaitingReturn: { color: colors.analysis, text: 'Sem retorno ainda' },
  waitingClient: { color: colors.analysis, text: 'Aguardando cliente' },
  ocrNotStarted: { color: colors.updated, text: 'OCR não iniciado' },
  communicationFailure: { color: colors.canceled, text: 'Falha de comunicação' }
}

const roleIcon: { [role in _participantRole]: JSX.Element } = {
  emitente: <UserOutlined />,
  conjuge: <TeamOutlined />,
  alienante: <HomeOutlined />,
  garantidor: <SafetyCertificateOutlined />,
  pessoa: <SolutionOutlined />,
  proprietario: <HomeOutlined />,
  terceiro: <IdcardOutlined />,
  socio: <IdcardOutlined />,
  outro: <IdcardOutlined />
}

const REVIEW_LABEL: { [review: string]: string } = {
  approve: 'Aprovado',
  approveWithRestriction: 'Aprovado com restrição',
  reject: 'Rejeitado'
}

function getInitials (name: string) {
  const parts = name.trim().split(' ').filter(Boolean)
  if (!parts.length) return '?'

  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''

  return `${first}${last}`.toUpperCase()
}

type _stepSummaryOrigin = 'automatic' | 'manual' | 'mixed'

// A mensagem diz se o passo fechou pelas consultas ou pelo parecer da Formalização
const STEP_SUMMARY: {
  [decision in _antifraudStepDecision]: {
    type: 'success' | 'error' | 'warning'
    message: string
    manual?: string
    mixed?: string
  }
} = {
  approved: {
    type: 'success',
    message: 'Passo aprovado automaticamente — todos os envolvidos estão aprovados.',
    manual: 'Passo aprovado manualmente — todos os envolvidos foram aprovados pelo parecer da Formalização.',
    mixed: 'Passo aprovado — todos os envolvidos estão aprovados, parte deles pelo parecer da Formalização.'
  },
  reproved: {
    type: 'error',
    message: 'Passo reprovado automaticamente — todos os envolvidos foram reprovados.',
    manual: 'Passo reprovado manualmente — todos os envolvidos foram rejeitados pelo parecer da Formalização.',
    mixed: 'Passo reprovado — todos os envolvidos foram reprovados, parte deles pelo parecer da Formalização.'
  },
  analysis: {
    type: 'warning',
    message: 'Passo em análise — só aprova com todos os envolvidos aprovados e só reprova com todos reprovados.'
  }
}

function summaryOrigin (summary: IAntifraudStepSummary): _stepSummaryOrigin {
  const decided = summary.decision === 'reproved' ? summary.rejected : summary.approved

  if (!summary.byReview.length) return 'automatic'

  return decided.every(label => summary.byReview.includes(label)) ? 'manual' : 'mixed'
}

function StepSummary ({ summary }: { summary: IAntifraudStepSummary }) {
  const summaryText = STEP_SUMMARY[summary.decision]
  const origin = summaryOrigin(summary)
  const headline = (origin !== 'automatic' && summaryText[origin]) || summaryText.message

  const lines: string[] = []

  if (summary.approved.length) lines.push(`Aprovados: ${summary.approved.join(', ')}.`)
  if (summary.rejected.length) lines.push(`Reprovados: ${summary.rejected.join(', ')}.`)

  if (origin === 'mixed') lines.push(`Pelo parecer da Formalização: ${summary.byReview.join(', ')}.`)

  if (summary.awaitingReview.length) {
    lines.push(`Pendentes de validação manual: ${summary.awaitingReview.join(', ')}.`)
  }

  if (summary.awaitingReturn.length) {
    lines.push(`Aguardando retorno das consultas: ${summary.awaitingReturn.join(', ')}.`)
  }

  return (
    <Alert
      className={`${STYLE}step-summary`}
      type={summaryText.type}
      showIcon
      message={headline}
      description={lines.length ? lines.join(' ') : undefined}
    />
  )
}

function Farol ({ color }: { color: string }) {
  return <span className={`${STYLE}farol`} style={{ backgroundColor: color }} />
}

function ProcessesTable ({
  processes,
  onResend,
  resendingSolution,
  resendBlocked,
  actionsEnabled,
  afterUnicoResendEnabled
}: {
  processes: IAntifraudProcess[]
  onResend: (solution: _resendableSolution) => void
  resendingSolution: string
  resendBlocked: boolean
  actionsEnabled: boolean
  afterUnicoResendEnabled: boolean
}) {
  const columns: ColumnsType<IAntifraudProcess> = useMemo(() => [
    {
      title: 'Solução',
      dataIndex: 'solution',
      key: 'solution',
      width: 150,
      render: (solution: IAntifraudProcess['solution']) => renderBadge(solution, 'rodobensKYC')
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (type: string, process: IAntifraudProcess) => (
        <span>
          {type}
          {process.isV2 && <span className={`${STYLE}v2`}>v2</span>}
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 210,
      render: (_: unknown, process: IAntifraudProcess) => (
        <span className={`${STYLE}score ${STYLE}score--${process.communicationError ? 'communicationFailure' : process.status}`}>
          {process.description}
        </span>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      align: 'right',
      render: (_: unknown, process: IAntifraudProcess) => {
        if (!processResendEnabled(process, { actionsEnabled, afterUnicoResendEnabled })) return null

        return (
          <Button
            className={`${STYLE}resend-button`}
            size="small"
            loading={resendingSolution === process.resendKey}
            disabled={resendBlocked && resendingSolution !== process.resendKey}
            onClick={() => process.resendKey && onResend(process.resendKey)}
          >
            <ReloadOutlined /> Reenviar
          </Button>
        )
      }
    }
  ], [onResend, resendingSolution, resendBlocked, actionsEnabled, afterUnicoResendEnabled])

  return (
    <Table<IAntifraudProcess>
      className={`${STYLE}table`}
      columns={columns}
      dataSource={processes}
      rowKey={process => `${process.solution}-${process.type}`}
      pagination={false}
      scroll={{ x: 700 }}
      size="small"
    />
  )
}

function ReviewRecord ({
  review,
  showEvidences
}: {
  review: IAntifraudSavedReview
  showEvidences: boolean
}) {
  return (
    <div className={`${STYLE}review-record`}>
      <span className={`${STYLE}review-record-head`}>
        <strong>{REVIEW_LABEL[review.review] ?? review.review}</strong>
        <span className={`${STYLE}hint`}>
          {`${review.userName} · ${timeAsDayjs(review.createdAt).format('DD/MM/YYYY [às] HH:mm')}`}
        </span>
      </span>

      {review.comment && <p className={`${STYLE}review-record-comment`}>{review.comment}</p>}

      {showEvidences && review.evidences.length > 0 && (
        <div className={`${STYLE}saved-evidences`}>
          <span className={`${STYLE}hint`}>Evidências</span>

          <ul>
            {review.evidences.map((evidence, index) => (
              <li key={`${evidence.path}-${index}`}>
                <PaperClipOutlined />

                {evidence.url
                  ? <a href={evidence.url} target="_blank" rel="noopener noreferrer">{evidence.name}</a>
                  : <span>{evidence.name}</span>
                }
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function ManualReview ({
  manualReview,
  history,
  canRegister,
  onToggle,
  onChangeReview,
  onChangeComment,
  onChangeEvidences,
  onSave
}: {
  manualReview: IAntifraudManualReviewState
  history: IAntifraudSavedReview[]
  canRegister: boolean
  onToggle: (open: boolean) => void
  onChangeReview: (review: _antifraudManualReview) => void
  onChangeComment: (comment: string) => void
  onChangeEvidences: (evidences: File[]) => void
  onSave: () => void
}) {
  const isRestriction = requiresJustification(manualReview.review)
  const requiredClass = isRestriction ? ` ${STYLE}field--required` : ''

  const [last, ...older] = history

  if (!last && !canRegister) return null

  return (
    <div className={`${STYLE}manual-review`}>
      <div className={`${STYLE}manual-review-header`}>
        <span className={`${STYLE}manual-review-title`}>
          <strong>Validação manual</strong>
          <span>
            {last
              ? 'Último parecer registrado para este participante'
              : 'Registre um parecer quando a análise automática não for suficiente'
            }
          </span>
        </span>

        {canRegister && !manualReview.open && (
          <Button type="primary" size="small" onClick={() => onToggle(true)}>
            <PlusOutlined /> Adicionar novo parecer
          </Button>
        )}
      </div>

      {last && (
        <div className={`${STYLE}manual-review-records`}>
          <ReviewRecord review={last} showEvidences />

          {older.length > 0 && (
            <Collapse ghost className={`${STYLE}review-older`}>
              <Collapse.Panel key="older" header={`Pareceres anteriores (${older.length})`}>
                {older.map((review, index) => (
                  <ReviewRecord key={`${review.createdAt}-${index}`} review={review} showEvidences={false} />
                ))}
              </Collapse.Panel>
            </Collapse>
          )}
        </div>
      )}

      {canRegister && manualReview.open && (
        <div className={`${STYLE}manual-review-body`}>
          <div className={`${STYLE}grid`}>
            <div className={`${STYLE}field`}>
              <label>Aprovar validação</label>

              <Select
                value={manualReview.review || undefined}
                placeholder="Selecione..."
                onChange={onChangeReview}
              >
                <Select.Option value="approve">Aprovar</Select.Option>
                <Select.Option value="approveWithRestriction">Aprovar com restrição</Select.Option>
                <Select.Option value="reject">Rejeitar</Select.Option>
              </Select>

              <span className={`${STYLE}hint`}>
                Fica registrado com seu login e a data da decisão.
              </span>

              {manualReview.errors.review && (
                <span className={`${STYLE}error`}>{manualReview.errors.review}</span>
              )}
            </div>

            <div className={`${STYLE}field${requiredClass}`}>
              <label>Parecer da área{isRestriction ? '' : ' (opcional)'}</label>

              <TextArea
                rows={4}
                value={manualReview.comment}
                onChange={event => onChangeComment(event.target.value)}
                placeholder={isRestriction
                  ? 'Descreva a restrição e o que sustenta a aprovação.'
                  : 'Opcional. Descreva o que motivou a decisão.'
                }
              />

              {manualReview.errors.comment && (
                <span className={`${STYLE}error`}>{manualReview.errors.comment}</span>
              )}
            </div>
          </div>

          <div className={`${STYLE}field${requiredClass}`}>
            <label>Evidências</label>

            <Upload.Dragger
              multiple
              beforeUpload={() => false}
              fileList={manualReview.evidences.map((file, index) => ({
                uid: String(index),
                name: file.name,
                status: 'done',
                originFileObj: file
              })) as any}
              onChange={({ fileList }) => onChangeEvidences(
                fileList.map(item => item.originFileObj).filter(Boolean) as File[]
              )}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className={`${STYLE}upload-title`}>Arraste os arquivos ou clique para selecionar</p>
              <p className={`${STYLE}hint`}>PDF, PNG ou JPG · até 10 MB por arquivo</p>
            </Upload.Dragger>

            {manualReview.errors.evidences && (
              <span className={`${STYLE}error`}>{manualReview.errors.evidences}</span>
            )}
          </div>

          <div className={`${STYLE}manual-review-actions`}>
            <Button type="primary" size="small" onClick={() => onToggle(false)}>Cancelar</Button>

            <Button
              type="primary"
              loading={manualReview.saving}
              onClick={onSave}
            >
              Registrar parecer
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function ParticipantPanel ({
  participant,
  manualReview,
  onToggleManualReview,
  onChangeReview,
  onChangeComment,
  onChangeEvidences,
  onSaveReview,
  onResend,
  onDownloadReport,
  onShowHistory,
  downloadingReport,
  resendingSolution,
  resendBlocked,
  onReconcile,
  reconciling,
  actionsEnabled,
  afterUnicoResendEnabled,
  unicoLinkResendEnabled,
  canRegisterReview
}: {
  participant: IAntifraudParticipant
  manualReview: IAntifraudManualReviewState
  onToggleManualReview: (open: boolean) => void
  onChangeReview: (review: _antifraudManualReview) => void
  onChangeComment: (comment: string) => void
  onChangeEvidences: (evidences: File[]) => void
  onSaveReview: () => void
  onResend: (solution: _resendableSolution) => void
  onDownloadReport: () => void
  onShowHistory: () => void
  downloadingReport: boolean
  resendingSolution: string
  resendBlocked: boolean
  onReconcile: () => void
  reconciling: boolean
  actionsEnabled: boolean
  afterUnicoResendEnabled: boolean
  unicoLinkResendEnabled: boolean
  canRegisterReview: boolean
}) {
  const info = participantInfo[participant.status]

  return (
    <div className={`${STYLE}panel`}>
      <div className={`${STYLE}identity`}>
        <span className={`${STYLE}avatar`}>{getInitials(participant.name)}</span>

        <span className={`${STYLE}identity-name`}>
          <b>{participant.name}</b>
          <span>{[participant.cpf, participant.role].filter(Boolean).join(' · ')}</span>
        </span>

        <span className={`${STYLE}identity-spacer`} />

        <span
          className={`${STYLE}pill`}
          style={{ backgroundColor: info.color, color: getTextColor(info.color) }}
        >
          <Farol color={getTextColor(info.color)} />
          {info.text}
        </span>

        <span className={`${STYLE}stamp`}>{participant.timestamp}</span>
      </div>

      <div className={`${STYLE}panel-body`}>
        {participant.banner && (
          <Alert
            className="mb-3"
            type={participant.banner.type}
            showIcon
            message={participant.banner.title}
            description={participant.banner.description}
          />
        )}

        {showsFraudWarning(participant) && (
          <Alert
            className="mb-3"
            type="warning"
            showIcon
            message="A pontuação liberou, mas o retorno traz alerta de fraude."
            description={
              <ul className={`${STYLE}alert-list`}>
                {participant.alerts.map(alert => <li key={alert}>{alert}</li>)}
              </ul>
            }
          />
        )}

        <div className={`${STYLE}panel-header`}>
          <span className={`${STYLE}panel-title`}>
            <strong>Processos antifraude</strong>
            <span>Confira o status de cada processo antifraude</span>
          </span>

          <span className={`${STYLE}panel-actions`}>
            <Button type="link" size="small" onClick={onShowHistory}>
              <HistoryOutlined /> Ver histórico
            </Button>

            {actionsEnabled && canReconcileUnico(participant.processes, { expired: participant.unicoExpired }) && (
              <Tooltip title="Erro para obter informações do Único. Tente novamente.">
                <Button
                  type="link"
                  size="small"
                  loading={reconciling}
                  onClick={onReconcile}
                >
                  <CloudSyncOutlined /> Consultar Único
                </Button>
              </Tooltip>
            )}

            {unicoLinkResendEnabled && (
              <Tooltip title="Reenviar abre um processo novo e o participante refaz a biometria pelo link.">
                <Button
                  type="link"
                  size="small"
                  loading={resendingSolution === 'unico'}
                  disabled={resendBlocked && resendingSolution !== 'unico'}
                  onClick={() => onResend('unico')}
                >
                  <ReloadOutlined /> Reenviar Único
                </Button>
              </Tooltip>
            )}

            {actionsEnabled && canResendEmailAge(participant.processes) && (
              <Tooltip title="Uma consulta do EmailAge devolve a validação do e-mail e a dos dados complementares.">
                <Button
                  type="link"
                  size="small"
                  loading={resendingSolution === 'emailAge'}
                  disabled={resendBlocked && resendingSolution !== 'emailAge'}
                  onClick={() => onResend('emailAge')}
                >
                  <ReloadOutlined /> Reenviar EmailAge
                </Button>
              </Tooltip>
            )}

            <Button
              type="primary"
              size="small"
              loading={downloadingReport}
              onClick={onDownloadReport}
            >
              <DownloadOutlined /> Baixar relatório
            </Button>
          </span>
        </div>

        <ProcessesTable
          processes={participant.processes}
          onResend={onResend}
          resendingSolution={resendingSolution}
          resendBlocked={resendBlocked}
          actionsEnabled={actionsEnabled}
          afterUnicoResendEnabled={afterUnicoResendEnabled}
        />

        <ManualReview
          manualReview={manualReview}
          history={participant.reviewHistory ?? []}
          canRegister={canRegisterReview}
          onToggle={onToggleManualReview}
          onChangeReview={onChangeReview}
          onChangeComment={onChangeComment}
          onChangeEvidences={onChangeEvidences}
          onSave={onSaveReview}
        />
      </div>
    </div>
  )
}

function AntifraudAnalysis ({
  participants: injected,
  onStepStatusChange
}: {
  participants?: IAntifraudParticipant[]
  // O passo muda de status no line-server (parecer, reenvio ou retorno das APIs): a proposta precisa
  // ser recarregada para a lista de passos mostrar o status novo sem atualizar a página
  onStepStatusChange?: () => void
}) {
  const proposal = useProposal()
  const user = useAuth()
  const currentStep = useStep()
  const proposalId = proposal.id
  const knownStepStatus = useRef<string | undefined>(currentStep.status)

  // Em ref para não refazer a busca a cada render do pai, que recria a função
  const stepStatusChanged = useRef(onStepStatusChange)
  stepStatusChanged.current = onStepStatusChange

  const [fetched, setFetched] = useState<IAntifraudParticipant[]>([])
  const [loading, setLoading] = useState<boolean>(!injected)
  const [error, setError] = useState<string>('')
  const [resendingRef, setResendingRef] = useState<string>('')
  const [resendingSolution, setResendingSolution] = useState<string>('')
  const [reconcilingRef, setReconcilingRef] = useState<string>('')
  const [downloadingReport, setDownloadingReport] = useState<boolean>(false)
  const [step, setStep] = useState<{ status?: string, approvedAt?: string, automaticDecision?: boolean }>({})
  const [proposalStatus, setProposalStatus] = useState<string>('')
  const [history, setHistory] = useState<{
    open: boolean
    loading: boolean
    label: string
    entries: IAntifraudSavedReview[]
    apiEvents: IAntifraudApiEvent[]
  }>({ open: false, loading: false, label: '', entries: [], apiEvents: [] })
  const [manualReviews, setManualReviews] = useState<{ [participantId: string]: IAntifraudManualReviewState }>({})

  const participants = injected ?? fetched

  const fetchParticipants = useCallback(async () => {
    if (!proposalId) {
      setError('Identificador da proposta não encontrado.')
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await AntifraudEgiRepository.participants(proposalId)

      const nextStep = response.data.data?.step ?? {}

      setFetched(mapParticipants(response.data.data))
      setStep(nextStep)
      setProposalStatus(response.data.data?.proposalStatus ?? '')

      if (nextStep.status && nextStep.status !== knownStepStatus.current) {
        knownStepStatus.current = nextStep.status
        stepStatusChanged.current?.()
      }
    } catch (err) {
      console.error('[antifraude] falha ao carregar participantes', err)
      setError(err?.message ?? 'Não foi possível carregar o antifraude.')
    } finally {
      setLoading(false)
    }
  }, [proposalId])

  useEffect(() => {
    if (injected) return

    fetchParticipants()
  }, [injected, fetchParticipants])

  const emptyManualReview = (): IAntifraudManualReviewState => ({
    open: false,
    review: '',
    comment: '',
    evidences: [],
    saving: false,
    errors: {}
  })

  const getManualReview = (participantId: string): IAntifraudManualReviewState => (
    manualReviews[participantId] ?? emptyManualReview()
  )

  const discardManualReview = (participantId: string) => {
    setManualReviews(previous => ({ ...previous, [participantId]: emptyManualReview() }))
  }

  const updateManualReview = (participantId: string, values: Partial<IAntifraudManualReviewState>) => {
    setManualReviews(previous => ({
      ...previous,
      [participantId]: { ...getManualReview(participantId), ...previous[participantId], ...values }
    }))
  }

  const handleResend = async (participantRef: string, solution: _resendableSolution) => {
    if (!proposalId || resendingRef) return

    setResendingRef(participantRef)
    setResendingSolution(solution)

    try {
      const response = await AntifraudEgiRepository.resendParticipant(proposalId, participantRef, solution)

      message.success(response.data.message ?? 'Antifraude reenviado!')
      await fetchParticipants()
    } catch (err) {
      message.error(err?.message ?? 'Não foi possível reenviar o antifraude.')
    } finally {
      setResendingRef('')
      setResendingSolution('')
    }
  }

  const handleReconcileUnico = async (participant: IAntifraudParticipant) => {
    if (!proposalId || reconcilingRef) return

    if (!canReconcileUnico(participant.processes, { expired: participant.unicoExpired })) {
      message.info('O Único deste participante já tem retorno.')
      return
    }

    setReconcilingRef(participant.ref)

    try {
      const response = await AntifraudEgiRepository.reconcileUnico(proposalId, participant.ref)

      message.success(response.data.message ?? 'Consulta ao Único concluída!')
      await fetchParticipants()
    } catch (err) {
      message.error(err?.message ?? 'Não foi possível consultar o Único.')
    } finally {
      setReconcilingRef('')
    }
  }

  const handleSaveReview = async (participantRef: string, participantId: string) => {
    if (!proposalId) return

    const current = getManualReview(participantId)
    const errors = validateManualReview(current)

    if (hasErrors(errors)) {
      updateManualReview(participantId, { errors })
      return
    }

    const data = new FormData()

    data.append('review', current.review)
    if (current.comment.trim()) data.append('comment', current.comment.trim())
    current.evidences.forEach(file => data.append('evidences', file))

    updateManualReview(participantId, { saving: true, errors: {} })

    try {
      await AntifraudEgiRepository.saveManualReview(proposalId, participantRef, data)

      message.success('Parecer registrado!')
      await fetchParticipants()
      setManualReviews(previous => {
        const next = { ...previous }
        delete next[participantId]
        return next
      })
    } catch (err) {
      const invalid = (err?.data?.invalid ?? []) as Array<{ field: string, message: string }>

      if (invalid.length) {
        updateManualReview(participantId, {
          saving: false,
          errors: invalid.reduce<IAntifraudManualReviewState['errors']>((errors, item) => {
            errors[item.field as keyof IAntifraudManualReviewState['errors']] = item.message
            return errors
          }, {})
        })
        return
      }

      updateManualReview(participantId, { saving: false })
      message.error(err?.message ?? 'Não foi possível registrar o parecer.')
    }
  }

  const handleShowHistory = async (participantRef: string, label: string) => {
    if (!proposalId) return

    setHistory({ open: true, loading: true, label, entries: [], apiEvents: [] })

    try {
      const response = await AntifraudEgiRepository.manualReviewHistory(proposalId, participantRef)

      setHistory({
        open: true,
        loading: false,
        label,
        entries: response.data.data?.history ?? [],
        apiEvents: response.data.data?.apiEvents ?? []
      })
    } catch (err) {
      setHistory({ open: false, loading: false, label: '', entries: [], apiEvents: [] })
      message.error(err?.message ?? 'Não foi possível carregar o histórico.')
    }
  }

  const handleDownloadReport = async () => {
    setDownloadingReport(true)

    try {
      const url = await generateAntifraudReport(buildReportData(proposal, participants, step))

      window.open(url, '_blank')
    } catch (err) {
      console.error('[antifraude] falha ao gerar o relatório', err)
      message.error('Não foi possível gerar o relatório.')
    } finally {
      setDownloadingReport(false)
    }
  }

  const canAct = canActOnAntifraud(user.level, user.areaName)
  const actionsEnabled = canAct && !resendIsBlocked(step.status, proposalStatus)
  const afterUnicoResendEnabled = canAct && acertPixResendAfterUnicoAllowed(proposalStatus)
  const unicoLinkResendEnabled = canAct && !resendIsBlocked(step.status, proposalStatus, {
    allowAutomaticReproval: true,
    automaticDecision: step.automaticDecision
  })

  if (loading) return <Skeleton active paragraph={{ rows: 8 }} />

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Erro ao carregar o antifraude"
        description={error}
        action={<Button size="small" onClick={fetchParticipants}>Tentar novamente</Button>}
      />
    )
  }

  if (!participants.length) {
    return (
      <Alert
        type="info"
        showIcon
        message="Consultas disparadas, ainda sem retorno."
        description="As validações foram enviadas às soluções e nenhuma resposta chegou até agora. Recarregue em alguns instantes."
      />
    )
  }

  return (
    <div className="antifraud">
      <Modal
        visible={history.open}
        title={`Histórico · ${history.label}`}
        footer={null}
        onCancel={() => setHistory({ open: false, loading: false, label: '', entries: [], apiEvents: [] })}
      >
        {history.loading && <Skeleton active paragraph={{ rows: 4 }} />}

        {!history.loading && !history.entries.length && !history.apiEvents.length && (
          <p className={`${STYLE}hint`}>Nenhum parecer ou chamada de API registrada para este participante.</p>
        )}

        {!history.loading && (history.entries.length > 0 || history.apiEvents.length > 0) && (
          <div className={`${STYLE}history`}>
            {historyTimeline(history.entries, history.apiEvents).map((item, index) => {
              if (item.type === 'api') {
                const { event } = item

                return (
                  <div className={`${STYLE}history-entry ${STYLE}history-entry--api`} key={index}>
                    <span className={`${STYLE}history-head`}>
                      <strong>{apiEventTitle(event)}</strong>
                      <span className={`${STYLE}hint`}>
                        {[event.userName, timeAsDayjs(event.createdAt).format('DD/MM/YYYY [às] HH:mm')].filter(Boolean).join(' · ')}
                      </span>
                    </span>

                    <span className={`${STYLE}hint`}>{event.trigger}</span>

                    {event.changes.length > 0 && (
                      <ul className={`${STYLE}history-changes`}>
                        {event.changes.map(change => (
                          <li key={change.field}>
                            <strong>{change.label}:</strong> {changeText(change)}
                          </li>
                        ))}
                      </ul>
                    )}

                    <ul className={`${STYLE}history-result`}>
                      {apiEventResultLines(event).map(line => <li key={line}>{line}</li>)}
                    </ul>

                    {event.resultAt && (
                      <span className={`${STYLE}hint`}>
                        {`Retorno em ${timeAsDayjs(event.resultAt).format('DD/MM/YYYY [às] HH:mm')}`}
                      </span>
                    )}
                  </div>
                )
              }

              const entry = item.review

              return (
                <div className={`${STYLE}history-entry`} key={index}>
                  <span className={`${STYLE}history-head`}>
                    <strong>{`Parecer: ${REVIEW_LABEL[entry.review] ?? entry.review}`}</strong>
                    <span className={`${STYLE}hint`}>
                      {`${entry.userName} · ${timeAsDayjs(entry.createdAt).format('DD/MM/YYYY [às] HH:mm')}`}
                    </span>
                  </span>

                  {entry.comment && <p className={`${STYLE}history-comment`}>{entry.comment}</p>}

                  {entry.evidences.length > 0 && (
                    <div className={`${STYLE}saved-evidences`}>
                      <span className={`${STYLE}hint`}>Evidências</span>

                      <ul>
                        {entry.evidences.map((evidence, position) => (
                          <li key={`${evidence.path}-${position}`}>
                            <PaperClipOutlined />

                            {evidence.url
                              ? <a href={evidence.url} target="_blank" rel="noopener noreferrer">{evidence.name}</a>
                              : <span>{evidence.name}</span>
                            }
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Modal>

      <StepSummary summary={summarizeAntifraudStep(participants)} />

      <Tabs className={`${STYLE}tabs`}>
        {participants.map(participant => (
          <Tabs.TabPane
            key={participant.id}
            tab={
              <span className={`${STYLE}tab`}>
                <Farol color={participantInfo[participantTabStatus(participant)].color} />
                <span className={`${STYLE}tab-icon`}>
                  {roleIcon[participantRole(participant.ref)]}
                </span>
                {participant.tab}
                {showsFraudWarning(participant) && (
                  <WarningOutlined className={`${STYLE}tab-warning`} />
                )}
              </span>
            }
          >
            <ParticipantPanel
              participant={participant}
              manualReview={getManualReview(participant.id)}
              onToggleManualReview={open => (open
                ? updateManualReview(participant.id, { open })
                : discardManualReview(participant.id))}
              onChangeReview={review => updateManualReview(participant.id, { review, errors: {} })}
              onChangeComment={comment => updateManualReview(participant.id, { comment, errors: {} })}
              onChangeEvidences={evidences => updateManualReview(participant.id, { evidences, errors: {} })}
              onSaveReview={() => handleSaveReview(participant.ref, participant.id)}
              onResend={(solution) => handleResend(participant.ref, solution)}
              resendBlocked={Boolean(resendingRef)}
              actionsEnabled={actionsEnabled}
              afterUnicoResendEnabled={afterUnicoResendEnabled}
              unicoLinkResendEnabled={unicoLinkResendEnabled}
              canRegisterReview={canAct}
              onReconcile={() => handleReconcileUnico(participant)}
              reconciling={reconcilingRef === participant.ref}
              onDownloadReport={handleDownloadReport}
              onShowHistory={() => handleShowHistory(participant.ref, participant.tab)}
              downloadingReport={downloadingReport}
              resendingSolution={resendingRef === participant.ref ? resendingSolution : ''}
            />
          </Tabs.TabPane>
        ))}
      </Tabs>
    </div>
  )
}

export default AntifraudAnalysis
